import React, { useState, useEffect } from 'react';
import Step1_IncomeMethod from './Step1_IncomeMethod';
import Step2A_OwnedStocks from './Step2A_OwnedStocks';
import Step2A_ShareGoal from './Step2A_ShareGoal';
import Step2B_CompanyType from './Step2B_CompanyType';
import Step2B_RiskComfort from './Step2B_RiskComfort';
import { useBeginnerProgress } from '../../../contexts/BeginnerProgressContext';

export interface WizardFilters {
  // Stock filters (Path A vs Path B)
  ownedStocks?: string[];

  // Contract filters (DTE and Delta ranges)
  minDays: number;
  maxDays: number;
  minDelta: number;
  maxDelta: number;

  // Company quality filters (Path B only)
  min_net_margin?: number;
  max_debt_equity?: number;
  min_iv_rank?: number;
  min_rsi?: number;
  max_rsi?: number;
  trend_filter?: string;
  max_spread_pct?: number;
}

interface BeginnerFlowWizardProps {
  onComplete: (filters: WizardFilters) => void;
}

const BeginnerFlowWizard: React.FC<BeginnerFlowWizardProps> = ({ onComplete }) => {
  // Global progress tracking
  const { setStep, setPath, markStepComplete } = useBeginnerProgress();

  // Step tracking
  const [currentStepId, setCurrentStepId] = useState<string>('1');
  const totalSteps = 5; // Total: 3 wizard questions + 1 stock selection + 1 contract selection

  // User answers
  const [incomeMethod, setIncomeMethod] = useState<'existing' | 'new' | null>(null);
  const [ownedStocks, setOwnedStocks] = useState<string[]>([]);
  const [shareGoal, setShareGoal] = useState<'keep' | 'sell' | null>(null);
  const [companyType, setCompanyType] = useState<'premium' | 'solid' | 'any' | null>(null);
  const [riskComfort, setRiskComfort] = useState<'low' | 'medium' | 'high' | null>(null);

  // Load saved preferences on component mount
  useEffect(() => {
    const loadSavedPreferences = async () => {
      try {
        const { apiService } = await import('../../../services/api');
        const preferences = await apiService.getUserPreferences();

        // Pre-populate owned stocks if they exist
        if (preferences?.owned_stocks && Array.isArray(preferences.owned_stocks)) {
          setOwnedStocks(preferences.owned_stocks);
        }
      } catch (error) {
        // Silently fail - user can still enter stocks manually
        console.error('Failed to load saved preferences:', error);
      }
    };

    loadSavedPreferences();
  }, []); // Run once on mount

  // Determine current step number for progress bar
  const getCurrentStepNumber = () => {
    const stepMap: { [key: string]: number } = {
      '1': 1,
      '2A.1': 2,
      '2A.2': 3,
      '2B.1': 2,
      '2B.2': 3
    };
    return stepMap[currentStepId] || 1;
  };

  // Navigation handlers
  const handleStep1Select = (method: 'existing' | 'new') => {
    setIncomeMethod(method);
    setPath(method === 'existing' ? 'A' : 'B'); // Track user's path selection
    markStepComplete(1); // Mark step 1 complete
    // Auto-advance to next step
    setTimeout(() => {
      if (method === 'existing') {
        setCurrentStepId('2A.1');
      } else {
        setCurrentStepId('2B.1');
      }
    }, 300); // Small delay for UX smoothness
  };

  const handleStep2A1Complete = () => {
    markStepComplete(2); // Mark step 2 complete
    setCurrentStepId('2A.2');
  };

  const handleStep2A2Select = (goal: 'keep' | 'sell') => {
    setShareGoal(goal);
    markStepComplete(3); // Mark step 3 complete
    // Complete wizard with Path A filters
    completeWizard('A', goal);
  };

  const handleStep2B1Select = (type: 'premium' | 'solid' | 'any') => {
    setCompanyType(type);
    markStepComplete(2); // Mark step 2 complete
    setTimeout(() => {
      setCurrentStepId('2B.2');
    }, 300);
  };

  const handleStep2B2Select = (risk: 'low' | 'medium' | 'high') => {
    setRiskComfort(risk);
    markStepComplete(3); // Mark step 3 complete
    // Complete wizard with Path B filters
    completeWizard('B', null, risk);
  };

  const handleSkip = () => {
    // Skip wizard and show all stocks with no filters
    const emptyFilters: WizardFilters = {
      minDays: 0,
      maxDays: 365,
      minDelta: 0,
      maxDelta: 1
    };

    // Mark wizard as complete and jump to step 4
    markStepComplete(1);
    markStepComplete(2);
    markStepComplete(3);
    setStep(4);

    onComplete(emptyFilters);
  };

  const completeWizard = async (
    path: 'A' | 'B',
    shareGoalParam?: 'keep' | 'sell' | null,
    riskParam?: 'low' | 'medium' | 'high' | null
  ) => {
    let filters: WizardFilters = {
      minDays: 0,
      maxDays: 0,
      minDelta: 0,
      maxDelta: 0
    };

    if (path === 'A') {
      // Path A: Existing shares
      filters.ownedStocks = ownedStocks;

      // Map share goal to DTE/Delta
      if (shareGoalParam === 'keep') {
        filters.minDays = 30;
        filters.maxDays = 45;
        filters.minDelta = 0.10;
        filters.maxDelta = 0.25;
      } else if (shareGoalParam === 'sell') {
        filters.minDays = 7;
        filters.maxDays = 21;
        filters.minDelta = 0.20;
        filters.maxDelta = 0.40;
      }
    } else if (path === 'B') {
      // Path B: New shares

      // Map company type to quality filters
      switch (companyType) {
        case 'premium':
          filters.min_net_margin = 15;
          filters.max_debt_equity = 0.5;
          break;
        case 'solid':
          filters.min_net_margin = 5;
          filters.max_debt_equity = 1.0;
          break;
        case 'any':
          filters.min_net_margin = 0;
          filters.max_debt_equity = 1.5;
          break;
      }

      // Map risk comfort to DTE/Delta
      if (riskParam === 'low') {
        filters.minDays = 45;
        filters.maxDays = 90;
        filters.minDelta = 0.05;
        filters.maxDelta = 0.15;
      } else if (riskParam === 'medium') {
        filters.minDays = 30;
        filters.maxDays = 45;
        filters.minDelta = 0.15;
        filters.maxDelta = 0.25;
      } else if (riskParam === 'high') {
        filters.minDays = 7;
        filters.maxDays = 30;
        filters.minDelta = 0.25;
        filters.maxDelta = 0.40;
      }

      // Add strategy filters (same for all risk levels in Path B)
      filters.min_iv_rank = 30;
      filters.min_rsi = 50;
      filters.max_rsi = 70;
      filters.trend_filter = 'Bullish,Golden Cross';
      filters.max_spread_pct = 15;
    }

    // Save wizard responses to user preferences
    try {
      const { apiService } = await import('../../../services/api');
      await apiService.saveUserPreferences({
        income_method: incomeMethod,
        owned_stocks: ownedStocks.length > 0 ? ownedStocks : null,
        share_goal: shareGoalParam || null,
        company_type_preference: companyType || null,
        risk_comfort: riskParam || null
      });
    } catch (error) {
      console.error('Failed to save wizard preferences:', error);
      // Continue anyway - filtering will still work
    }

    onComplete(filters);
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStepId) {
      case '1':
        return (
          <Step1_IncomeMethod
            selectedMethod={incomeMethod}
            onSelect={handleStep1Select}
            onSkip={handleSkip}
          />
        );

      case '2A.1':
        return (
          <Step2A_OwnedStocks
            selectedStocks={ownedStocks}
            onStocksChange={setOwnedStocks}
            onSkip={handleStep2A1Complete}
            onContinue={handleStep2A1Complete}
          />
        );

      case '2A.2':
        return (
          <Step2A_ShareGoal
            selectedGoal={shareGoal}
            onSelect={handleStep2A2Select}
          />
        );

      case '2B.1':
        return (
          <Step2B_CompanyType
            selectedType={companyType}
            onSelect={handleStep2B1Select}
          />
        );

      case '2B.2':
        return (
          <Step2B_RiskComfort
            selectedRisk={riskComfort}
            onSelect={handleStep2B2Select}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="beginner-wizard-container">
      <div className="beginner-wizard-content">
        {renderCurrentStep()}
      </div>

      {/* Navigation buttons (optional - can use for back functionality) */}
      {currentStepId !== '1' && (
        <button
          className="wizard-back-button"
          onClick={() => {
            // Handle back navigation
            if (currentStepId === '2A.1' || currentStepId === '2B.1') {
              setCurrentStepId('1');
              setStep(1); // Update global step counter
            } else if (currentStepId === '2A.2') {
              setCurrentStepId('2A.1');
              setStep(2); // Update global step counter
            } else if (currentStepId === '2B.2') {
              setCurrentStepId('2B.1');
              setStep(2); // Update global step counter
            }
          }}
          aria-label="Go back to previous step"
        >
          ← Back
        </button>
      )}
    </div>
  );
};

export default BeginnerFlowWizard;
